import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    container: { maxWidth: '1200px', margin: '0 auto', padding: '0 24px' },
    nav: { borderBottom: '1px solid #e8eaed', padding: '16px 0', position: 'sticky' as const, top: 0, zIndex: 1000, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.95)' },
    navContainer: { maxWidth: '1200px', margin: '0 auto', padding: '0 16px', display: 'flex', justifyContent: isMobile ? 'flex-start' : 'space-between', alignItems: 'center', gap: isMobile ? '12px' : '0' },
    logo: { fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: '#14B8A6', textDecoration: 'none', letterSpacing: '-0.5px' },
    navLinks: { display: 'flex', gap: '32px', alignItems: 'center' },
    mobileMenu: { position: 'fixed' as const, top: 0, left: 0, width: '80%', maxWidth: '400px', height: '100vh', background: '#ffffff', zIndex: 2000, padding: '80px 24px 24px', display: 'flex', flexDirection: 'column' as const, gap: '24px' },
    mobileMenuOverlay: { position: 'fixed' as const, top: 0, left: 0, width: '100%', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1999 },
    hamburger: { background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    closeButton: { position: 'absolute' as const, top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    navLink: { color: '#5f6368', textDecoration: 'none', fontSize: '14px', fontWeight: 500, transition: 'color 0.2s' },
    primaryButton: { background: '#14B8A6', color: '#ffffff', padding: '12px 24px', borderRadius: '24px', textDecoration: 'none', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)' },
    content: { maxWidth: '780px', margin: '0 auto', padding: isMobile ? '40px 16px 60px' : '48px 24px 80px' },
    h2: { fontSize: '22px', fontWeight: 500, color: '#202124', marginTop: '40px', marginBottom: '16px' },
    h3: { fontSize: '17px', fontWeight: 500, color: '#202124', marginTop: '24px', marginBottom: '8px' },
    p: { fontSize: '15px', color: '#5f6368', lineHeight: 1.8, marginBottom: '16px' },
    ul: { paddingLeft: '20px', marginBottom: '16px' },
    li: { fontSize: '15px', color: '#5f6368', lineHeight: 1.8, marginBottom: '6px' },
  };

  return (
    <>
      <Helmet>
        <title>Privacy Policy — HUSLRZ</title>
        <meta name="description" content="HUSLRZ Privacy Policy. Learn how we collect, use, and protect your personal data in compliance with India's DPDP Act, 2023." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      </Helmet>

      <div style={{ fontFamily: 'Google Sans, Roboto, Arial, sans-serif', color: '#202124' }}>
        {/* Navigation */}
        <nav style={styles.nav}>
          <div style={styles.navContainer}>
            {isMobile && <button style={styles.hamburger} onClick={() => setMobileMenuOpen(true)}><Menu size={24} color="#202124" /></button>}
            <Link to="/" style={styles.logo}>HUSLRZ</Link>
            {!isMobile && (
              <div style={styles.navLinks}>
                <Link to="/partners" style={styles.navLink}>For Partners</Link>
                <Link to="/corporate" style={styles.navLink}>For Corporates</Link>
                <Link to="/about" style={styles.navLink}>About</Link>
                <Link to="/contact" style={styles.navLink}>Contact</Link>
                <Link to="/login" style={{ ...styles.navLink, color: '#14B8A6', fontWeight: 600 }}>Login</Link>
                <Link to="/signup" style={styles.primaryButton}>Get Started <ArrowRight size={16} /></Link>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobile && mobileMenuOpen && (
          <>
            <div style={styles.mobileMenuOverlay} onClick={() => setMobileMenuOpen(false)} />
            <div style={styles.mobileMenu}>
              <button style={styles.closeButton} onClick={() => setMobileMenuOpen(false)}><X size={24} color="#202124" /></button>
              <Link to="/partners" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Partners</Link>
              <Link to="/corporate" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Corporates</Link>
              <Link to="/about" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>About</Link>
              <Link to="/contact" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link to="/login" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>Login</Link>
              <Link to="/signup" style={{ ...styles.primaryButton, width: '100%', justifyContent: 'center', padding: '16px 24px', fontSize: '16px', marginTop: '16px' }} onClick={() => setMobileMenuOpen(false)}>Get Started <ArrowRight size={18} /></Link>
            </div>
          </>
        )}

        {/* Content */}
        <div style={styles.content}>
          <h1 style={{ fontSize: 'clamp(28px, 6vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '8px' }}>Privacy Policy</h1>
          <p style={{ fontSize: '14px', color: '#9CA3AF', marginBottom: '32px' }}>Last updated: March 2, 2026</p>

          <p style={styles.p}>
            HUSLRZ ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (collectively, the "Platform"). This policy is compliant with India's Digital Personal Data Protection (DPDP) Act, 2023 and the Information Technology Act, 2000.
          </p>

          <h2 style={styles.h2}>1. Information We Collect</h2>
          <h3 style={styles.h3}>Information you provide:</h3>
          <ul style={styles.ul}>
            <li style={styles.li}>Name, mobile number, and email address during account registration</li>
            <li style={styles.li}>Profile information (age, gender, fitness preferences) — optional</li>
            <li style={styles.li}>Payment information processed securely through our payment gateway (Razorpay) — we do not store card details</li>
            <li style={styles.li}>Booking history and activity preferences</li>
            <li style={styles.li}>Feedback, reviews, and support communications</li>
          </ul>
          <h3 style={styles.h3}>Information collected automatically:</h3>
          <ul style={styles.ul}>
            <li style={styles.li}>Device information (model, operating system, app version)</li>
            <li style={styles.li}>Location data (with your permission) to show nearby facilities</li>
            <li style={styles.li}>Usage data (pages viewed, features used, session duration)</li>
            <li style={styles.li}>IP address and general geographic location</li>
          </ul>

          <h2 style={styles.h2}>2. How We Use Your Information</h2>
          <p style={styles.p}>We use your information to:</p>
          <ul style={styles.ul}>
            <li style={styles.li}>Create and manage your account</li>
            <li style={styles.li}>Process bookings and payments</li>
            <li style={styles.li}>Show you relevant fitness facilities near you</li>
            <li style={styles.li}>Send booking confirmations, reminders, and important updates</li>
            <li style={styles.li}>Manage your HUSLRZ Wallet and credit balances</li>
            <li style={styles.li}>Improve our platform, features, and user experience</li>
            <li style={styles.li}>Detect and prevent fraud or security issues</li>
            <li style={styles.li}>Communicate promotions (only with your consent; you can opt out anytime)</li>
          </ul>

          <h2 style={styles.h2}>3. How We Share Your Information</h2>
          <p style={styles.p}>We do not sell your personal data. We share information only in these cases:</p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>With Partner Facilities:</strong> Your name and booking details are shared with the facility you book, solely for check-in purposes</li>
            <li style={styles.li}><strong>With Payment Processors:</strong> Transaction details are shared with Razorpay to process payments securely</li>
            <li style={styles.li}><strong>With Corporate Employers:</strong> If you use HUSLRZ through a corporate wellness programme, your employer receives only anonymised, aggregated utilisation reports — never your individual booking details</li>
            <li style={styles.li}><strong>Legal Requirements:</strong> When required by law, court order, or government authority</li>
            <li style={styles.li}><strong>Service Providers:</strong> With trusted service providers (hosting, analytics, notifications) bound by confidentiality agreements</li>
          </ul>

          <h2 style={styles.h2}>4. Your Rights (Under DPDP Act, 2023)</h2>
          <p style={styles.p}>As a Data Principal, you have the right to:</p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Access:</strong> Request a summary of your personal data we hold</li>
            <li style={styles.li}><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
            <li style={styles.li}><strong>Erasure:</strong> Request deletion of your account and personal data</li>
            <li style={styles.li}><strong>Withdraw Consent:</strong> Withdraw previously given consent at any time</li>
            <li style={styles.li}><strong>Grievance Redressal:</strong> File a complaint if you believe your data rights have been violated</li>
          </ul>
          <p style={styles.p}>
            To exercise any of these rights, email us at <a href="mailto:support@huslrz.com" style={{ color: '#14B8A6', textDecoration: 'none' }}>support@huslrz.com</a>. We will respond within 30 days.
          </p>

          <h2 style={styles.h2}>5. Data Security</h2>
          <p style={styles.p}>
            We implement industry-standard security measures to protect your data, including encryption of data in transit (TLS) and at rest (AES-256), secure cloud hosting on AWS (India region), role-based access controls, and regular security audits. While no system is 100% secure, we take reasonable precautions to protect your information.
          </p>

          <h2 style={styles.h2}>6. Data Retention</h2>
          <p style={styles.p}>
            We retain your personal data for as long as your account is active. If you delete your account, we will erase your personal data within 30 days, except where retention is required by law (e.g., financial records retained for 8 years as required by Indian tax law).
          </p>

          <h2 style={styles.h2}>7. Cookies & Tracking</h2>
          <p style={styles.p}>
            Our website uses essential cookies for functionality and analytics cookies to understand usage patterns. You can manage cookie preferences through your browser settings. Our mobile app uses analytics SDKs (Firebase Analytics) to track usage patterns in an anonymised manner.
          </p>

          <h2 style={styles.h2}>8. Children's Privacy</h2>
          <p style={styles.p}>
            HUSLRZ is not intended for children under 18 years of age. We do not knowingly collect personal data from minors. If you believe we have inadvertently collected data from a minor, please contact us immediately.
          </p>

          <h2 style={styles.h2}>9. Changes to This Policy</h2>
          <p style={styles.p}>
            We may update this Privacy Policy from time to time. We will notify you of material changes via email or in-app notification at least 7 days before the changes take effect. Continued use of the Platform after changes constitutes acceptance of the updated policy.
          </p>

          <h2 style={styles.h2}>10. Contact Us</h2>
          <p style={styles.p}>
            If you have questions about this Privacy Policy or our data practices, contact us at:
          </p>
          <p style={styles.p}>
            <strong>HUSLRZ</strong><br />
            Email: <a href="mailto:support@huslrz.com" style={{ color: '#14B8A6', textDecoration: 'none' }}>support@huslrz.com</a><br />
            Grievance Officer: <a href="mailto:support@huslrz.com" style={{ color: '#14B8A6', textDecoration: 'none' }}>support@huslrz.com</a><br />
            Chennai, Tamil Nadu, India
          </p>
        </div>

        {/* Footer */}
        <footer style={{ background: '#111827', padding: isMobile ? '40px 16px' : '48px 24px', color: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#14B8A6' }}>HUSLRZ</div>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <Link to="/privacy" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Privacy Policy</Link>
                <Link to="/terms" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Terms of Service</Link>
                <Link to="/contact" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Contact</Link>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #374151', marginTop: '24px', paddingTop: '24px', textAlign: 'center' as const }}>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>&copy; {new Date().getFullYear()} HUSLRZ. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
